import { useEffect } from "react";
import { createPortal } from "react-dom";

interface ErrorToastProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  variant?: "error" | "warning" | "info" | "success";
  position?: "top" | "bottom";
  duration?: number; // Auto-dismiss duration in ms (0 = no auto-dismiss)
}

export function ErrorToast({
  isOpen,
  onClose,
  message,
  variant = "error",
  position = "top",
  duration = 5000,
}: ErrorToastProps) {
  useEffect(() => {
    if (isOpen && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isOpen, duration, onClose]);

  if (!isOpen) return null;

  // Variant-specific styling
  const variantConfig = {
    error: {
      bg: "bg-red-500/90 dark:bg-red-600/90",
      icon: "error",
    },
    warning: {
      bg: "bg-orange-500/90 dark:bg-orange-600/90",
      icon: "warning",
    },
    info: {
      bg: "bg-blue-500/90 dark:bg-blue-600/90",
      icon: "info",
    },
    success: {
      bg: "bg-green-500/90 dark:bg-green-600/90",
      icon: "check_circle",
    },
  };

  const config = variantConfig[variant];

  // Position styles that account for iOS safe area (notch)
  // We add extra padding (1rem) on top of the safe area inset to ensure
  // the toast doesn't sit flush against the notch
  const positionStyle =
    position === "top"
      ? { top: "calc(env(safe-area-inset-top, 0px) + 1rem)" }
      : { bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" };

  const toastElement = (
    <div
      className={`fixed left-4 right-4 mx-auto z-[9999] flex items-center gap-3 ${config.bg} text-white px-4 py-3 rounded-2xl shadow-lg max-w-md animate-in fade-in slide-in-from-top-2`}
      style={positionStyle}
    >
      {/* Icon */}
      <span className="material-symbols-outlined text-2xl flex-shrink-0">
        {config.icon}
      </span>

      {/* Message */}
      <p className="text-sm font-medium flex-1">{message}</p>

      {/* Close Button */}
      <button
        onClick={onClose}
        className="flex items-center justify-center w-6 h-6 rounded-full hover:bg-white/20 transition-colors flex-shrink-0"
        aria-label="Close"
      >
        <span className="material-symbols-outlined text-lg">close</span>
      </button>
    </div>
  );

  // Use portal to render outside React tree, directly in body
  // This ensures the toast is positioned relative to the viewport
  return createPortal(toastElement, document.body);
}
